import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { MdiIcon, Button, CircularProgress, FormHelperText, FormControl, Grid } from '@mic3/platform-ui';
import { connect } from 'react-redux';
import { Typography } from '@material-ui/core';
import styled from 'styled-components';

import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { muiTheme } from 'app/themes/materialUi';
import DotMenu from 'app/components/molecules/DotMenu/DotMenu';
import ModalDialog from 'app/components/organisms/ModalDialog/ModalDialog';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import AttachmentIcon from 'app/containers/Common/Attachments/AttachmentIcon';
import { addEntityMedia, deleteEntityMedia, loadEntityMedia } from 'store/actions/entities/entitiesActions';
import { getUserReferences } from 'store/selectors/userSelectors';
import { showToastr } from 'store/actions/app/appActions';
import { ListItem, Name, Description } from 'app/components/molecules/UploadButton/UploadFileField';
import { fromNow } from 'app/utils/date/date';

// Subscription handling lives upstream (TaskFormTab). When that handler dispatches
// new media into the entityMedia slice, this component reacts via `componentDidUpdate`
// — see below — and discards any local unsaved edits so the incoming server state wins.
const Container = styled.div`
    margin: 1rem 0;
`;

const Details = styled.div`
    display: flex;
    flex-direction: column;
    margin-left: 1rem;
    width: 100%;
`;


const StyledTypography = styled(Typography)`
    font-family: Roboto !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    line-height: 16px !important;
    letter-spacing: 0.4px !important;
    padding-bottom: 5px !important;
    color: ${({ theme }) => theme.material.colors.text.secondary} !important;
`;

const UploadingText = styled.div`
    font-family: Roboto;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.25px;
    text-align: left;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    margin-left: 1.5rem;
    margin-top: 4px;
`;

const FileIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 4px;
    background: ${({ theme }) => theme.material.colors.leftSideBar.iconActive};
`;

const ErrorIcon = styled(MdiIcon)`
    color: ${({ theme }) => theme.material.colors.error.main};
    margin-left: 8px;
`;

const EditImage = styled.img`
    max-width: 300px;
    margin: 0 auto;
    display: block;
`;

const editFormFields = [
    { field: 'filename', type: 'text', properties: { name: 'filename', label: 'File name' }, constraints: { required: true } },
];

const kilobytesToBytes = kb => kb * 1024;

const getFileExtension = fileName => {
    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex === -1) return null;
    return `.${fileName.substring(lastDotIndex + 1).toLowerCase()}`;
};

const formatFileSize = bytes => {
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 Byte';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i), 2) + ' ' + sizes[i];
};

const isSvg = (mimeType, fileName) =>
    Boolean(mimeType?.includes('svg')) || getFileExtension(fileName || '') === '.svg';

const thumbnailStyle = { width: 40, height: 40, objectFit: 'cover', borderRadius: 4 };
const fileRowStyle = { display: 'flex', alignItems: 'center', width: '90%' };

// Saved SVGs are served by the media endpoint without a `Content-Type` header.
// Browsers content-sniff raster images but refuse to render SVG in <img> unless
// it is declared `image/svg+xml`, so the saved thumbnail breaks. Fetch the bytes
// and wrap them in a blob URL with the correct type (same mechanism that makes a
// freshly-staged File preview work). Raster thumbnails keep using the direct URL.
// INTERIM: the real fix belongs on the backend (send `Content-Type: image/svg+xml`
// on media responses) — that fixes every consumer with no per-thumbnail fetch.
const SvgThumbnail = ({ src, alt, fallback }) => {
    const [blobUrl, setBlobUrl] = React.useState(null);
    const [failed, setFailed] = React.useState(false);
    React.useEffect(() => {
        let url;
        let cancelled = false;
        setBlobUrl(null);
        setFailed(false);
        fetch(src)
            .then((res) => {
                // Without res.ok an error body (404/500 HTML) would be wrapped as
                // image/svg+xml and render as garbage — fail cleanly instead.
                if (!res.ok) throw new Error(`Media fetch failed: ${res.status}`);
                return res.text();
            })
            .then((text) => {
                if (cancelled) return;
                url = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
                setBlobUrl(url);
            })
            .catch(() => {
                if (!cancelled) setFailed(true);
            });
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [src]);
    if (failed) return fallback || null;
    if (!blobUrl) return null;
    return <img src={blobUrl} alt={alt} style={thumbnailStyle} />;
};



class MediaField extends PureComponent {
    state = {
        stagedFiles: [],       // array of { file: File }
        deletedFileIds: [],
        isUploading: false,
        errors: {},
        editingFile: null,
        editingIndex: null,
        editFormData: null,
        editImgSrc: null,
    };

    inputRef = React.createRef();
    editFormRef = React.createRef();

    componentDidMount() {
        const { registerMediaSave } = this.props;
        if (registerMediaSave) {
            registerMediaSave(this.flushChanges);
        }
        this.loadMedia();
    }

    componentDidUpdate(prevProps) {
        // When `savedMedia` changes from outside our own flush (e.g. TaskFormTab's
        // subscription handler dispatched a remote update), discard any local unsaved
        // edits so the incoming server state wins. Guarded by `isUploading` so a
        // mid-flight flush is not clobbered between mutation and reload.
        if (this.state.isUploading) return;
        if (prevProps.savedMedia === this.props.savedMedia) return;
        const hasPending = this.state.stagedFiles.length > 0
            || this.state.deletedFileIds.length > 0
            || Boolean(this.state.editingFile);
        if (!hasPending) return;
        this.setState({
            stagedFiles: [],
            deletedFileIds: [],
            errors: {},
            editingFile: null,
            editingIndex: null,
            editFormData: null,
            editImgSrc: null,
        });
    }

    @bind
    loadMedia() {
        const { entityId, entityType } = this.props;
        if (!entityId || !entityType) return;
        this.props.loadEntityMedia(entityType, entityId);
    }

    // Staged uploads/deletes live in local state and are persisted separately via
    // `flushChanges` on SAVE, so they never flow through the form's normal onChange
    // path. Without a touch signal the unsaved-changes (save/discard) dialog never
    // fires when the user uploads/removes media and navigates away.
    // FormGenerator's own `onFormTouched` only flips the *inner* form's touched flag
    // and never reaches TaskFormTab (which owns the PromptModal) unless an onChange
    // also runs — which media never triggers. So TaskFormTab injects a dedicated
    // `onMediaChanged` callback that flips its own `formTouched` directly.
    @bind
    markFormTouched() {
        const { onMediaChanged } = this.props;
        onMediaChanged?.();
    }

    @bind
    openFileDialog() {
        const { preview } = this.props;
        !preview && this.inputRef?.current && this.inputRef.current.click();
    }

    @bind
    handleFileSelect(event) {
        const { maxFileSize: maxFileSizeProp, acceptedExtensions, multiple, maxFiles: maxFilesProp } = this.props;
        const maxFiles = maxFilesProp ? Number(maxFilesProp) : undefined;
        const maxFileSize = maxFileSizeProp ? Number(maxFileSizeProp) : undefined;
        const files = Array.from(event.target.files);
        if (!files.length) return;

        const currentFiles = this.getMergedFiles();
        const validFiles = [];

        for (const file of files) {
            if (maxFileSize && file.size > kilobytesToBytes(maxFileSize)) {
                this.props.showToastr({
                    severity: 'error',
                    detail: `File "${file.name}" exceeds maximum size of ${maxFileSize} KB`,
                });
                continue;
            }
            if (acceptedExtensions?.length && !acceptedExtensions.includes(getFileExtension(file.name))) {
                this.props.showToastr({
                    severity: 'error',
                    detail: `Invalid file type for "${file.name}". Allowed: ${acceptedExtensions.join(', ')}`,
                });
                continue;
            }
            validFiles.push(file);
        }

        if (!multiple && validFiles.length > 0) {
            // Single file mode: replace any existing staged files, mark saved files for deletion
            this.setState(state => {
                const savedIds = (this.getSavedFiles() || [])
                    .map(f => f.fileId)
                    .filter(id => id && !state.deletedFileIds.includes(id));
                return {
                    stagedFiles: [{ file: validFiles[0] }],
                    deletedFileIds: [...state.deletedFileIds, ...savedIds],
                };
            });
            this.markFormTouched();
        } else if (validFiles.length > 0) {
            const available = maxFiles ? maxFiles - currentFiles.length : Infinity;
            // If the selection exceeds the remaining slots, reject the whole batch
            // (none added) and surface an error toastr. Native OS file pickers can't
            // cap selection count, so this is the enforcement point.
            if (validFiles.length > available) {
                this.props.showToastr({
                    severity: 'error',
                    detail: `Maximum ${maxFiles} files allowed. No files were added.`,
                });
            } else {
                this.setState(state => ({
                    stagedFiles: [...state.stagedFiles, ...validFiles.map(f => ({ file: f }))],
                }));
                this.markFormTouched();
            }
        }

        this.resetFileInput();
    }

    @bind
    resetFileInput() {
        try {
            this.inputRef.current.value = '';
        } catch (error) {}
    }

    @bind
    getSavedFiles() {
        const { name, savedMedia } = this.props;
        if (!savedMedia || !Array.isArray(savedMedia)) return [];
        return savedMedia.filter(m => m.mediaName === name);
    }

    @bind
    getMergedFiles() {
        const { deletedFileIds, stagedFiles } = this.state;
        const saved = this.getSavedFiles().filter(f => !deletedFileIds.includes(f.fileId));
        return [...saved, ...stagedFiles.map(s => s.file)];
    }

    @bind
    handleDelete(file, index) {
        if (file instanceof File) {
            const savedCount = this.getSavedFiles().filter(f => !this.state.deletedFileIds.includes(f.fileId)).length;
            const stagedIndex = index - savedCount;
            this.setState(state => ({
                stagedFiles: state.stagedFiles.filter((_, i) => i !== stagedIndex),
            }));
        } else {
            this.setState(state => ({ deletedFileIds: [...state.deletedFileIds, file.fileId] }));
        }
        this.markFormTouched();
    }

    @bind
    onDownload(file) {
        if (file instanceof File) return; // Can't download staged files
        const { entityId: id, entityType: type } = this.props;
        const { fileId } = file;
        if (!fileId || !id || !type) return;
        try {
            const url = `${window.location.origin}/media/entity/${type}/${id}/media/${fileId}`;
            const a = document.createElement('a');
            a.href = url;
            a.download = '';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading file:', error); // eslint-disable-line
        }
    }

    @bind
    async flushChanges() {
        const { entityId: id, entityType: type, name: mediaName } = this.props;
        const { stagedFiles, deletedFileIds } = this.state;
        if (!id || !type) return;
        if (!stagedFiles.length && !deletedFileIds.length) return;

        this.setState({ isUploading: true });

        const errors = {};

        // Delete files (only unique, valid IDs)
        const uniqueDeleteIds = [...new Set(deletedFileIds)].filter(Boolean);
        for (const fileId of uniqueDeleteIds) {
            try {
                const result = await this.props.deleteEntityMedia({ type, id, fileId });
                if (result instanceof Error) {
                    errors[fileId] = result.message;
                }
            } catch (error) {
                errors[fileId] = error.message;
            }
        }

        // Upload staged files
        for (const staged of stagedFiles) {
            try {
                const result = await this.props.addEntityMedia({ type, id, mediaName, file: staged.file });
                if (result instanceof Error) {
                    errors[staged.file.name] = result.message;
                }
            } catch (error) {
                errors[staged.file.name] = error.message;
            }
        }

        this.setState({
            isUploading: false,
            stagedFiles: [],
            deletedFileIds: [],
            errors,
        });

        // Reload media from server to get fresh data
        await this.loadMedia();

        return errors;
    }

    @bind
    @memoize()
    getUserData(userId, userReferences) {
        if (!userId) return null;
        if (typeof userId === 'object') return userId;
        return userReferences?.find(({ id }) => id === userId);
    }

    @bind
    isImageFile(mimeType, fileName) {
        if (mimeType?.startsWith('image/')) return true;
        if (fileName) {
            const ext = getFileExtension(fileName);
            return ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.webp', '.bmp', '.ico'].includes(ext);
        }
        return false;
    }

    getThumbnailSrc(file, isStaged) {
        if (isStaged) return URL.createObjectURL(file);
        const { entityId, entityType } = this.props;
        if (!entityId || !entityType || !file.fileId) return null;
        return `/media/entity/${entityType}/${entityId}/media/${file.fileId}`;
    }

    @bind
    openEditDetails(file, index) {
        const isStaged = file instanceof File;
        const fileName = isStaged ? file.name : file.fileName;
        let editImgSrc = null;
        if (isStaged && file.type?.startsWith('image/')) {
            editImgSrc = URL.createObjectURL(file);
        }
        this.setState({
            editingFile: file,
            editingIndex: index,
            editFormData: { filename: fileName.replace(/\.[^.]+$/, '') },
            editImgSrc,
        });
    }

    @bind
    closeEditDetails() {
        this.setState({ editingFile: null, editingIndex: null, editFormData: null, editImgSrc: null });
    }

    @bind
    applyEditDetails() {
        const formRef = this.editFormRef.current;
        if (!formRef) return;
        formRef.isValidForm().then(({ data, errors }) => {
            if (errors) return;
            const { editingFile, editingIndex } = this.state;
            if (!editingFile || !(editingFile instanceof File)) return;
            const savedCount = this.getSavedFiles().filter(f => !this.state.deletedFileIds.includes(f.fileId)).length;
            const stagedIndex = editingIndex - savedCount;
            const ext = editingFile.name.includes('.') ? editingFile.name.substring(editingFile.name.lastIndexOf('.')) : '';
            const newName = data.filename + ext;
            const newFile = new File([editingFile], newName, { type: editingFile.type });
            this.setState(state => {
                const stagedFiles = [...state.stagedFiles];
                stagedFiles[stagedIndex] = { file: newFile };
                return { stagedFiles, editingFile: null, editingIndex: null, editFormData: null, editImgSrc: null };
            });
            this.markFormTouched();
        });
    }

    renderEditModal() {
        const { editingFile, editFormData, editImgSrc } = this.state;
        if (!editingFile) return null;
        return (
            <ModalDialog
                title="Edit Details"
                onClose={this.closeEditDetails}
                footerButton={
                    <>
                        <Button variant="text" onClick={this.closeEditDetails}>Cancel</Button>
                        <Button onClick={this.applyEditDetails}>Apply</Button>
                    </>
                }
            >
                <Grid container justify="center" style={{ padding: '0 16px', marginBottom: 16 }}>
                    {editImgSrc
                        ? <EditImage alt={editingFile.name} src={editImgSrc} />
                        : <AttachmentIcon file={editingFile} />
                    }
                </Grid>
                <FormGenerator
                    components={editFormFields}
                    ref={this.editFormRef}
                    data={editFormData}
                    onChange={data => this.setState({ editFormData: data })}
                />
            </ModalDialog>
        );
    }

    buildMenuItems({ file, index, isStaged, canUpload, disabled }) {
        return [
            canUpload && { name: 'Upload new', icon: 'upload', onItemClick: this.openFileDialog },
            !isStaged && { name: 'Download', icon: 'download', onItemClick: () => this.onDownload(file) },
            isStaged && { name: 'Edit details', icon: 'pencil', onItemClick: () => this.openEditDetails(file, index) },
            !disabled && { name: 'Delete', icon: 'delete', onItemClick: () => this.handleDelete(file, index) },
        ].filter(Boolean);
    }

    renderFileItem(file, index, canUpload) {
        const { userReferences, disabled } = this.props;
        const isStaged = file instanceof File;

        const fileName = isStaged ? file.name : file.fileName;
        const fileSize = isStaged ? file.size : file.fileSize;
        const mimeType = isStaged ? file.type : file.mimeType;
        const isImage = this.isImageFile(mimeType, fileName);
        const thumbnailSrc = isImage ? this.getThumbnailSrc(file, isStaged) : null;

        let description = formatFileSize(fileSize);
        if (!isStaged && file.uploadedBy) {
            const user = this.getUserData(file.uploadedBy, userReferences);
            const userName = user?.name || 'Unknown';
            const uploadDate = fromNow(file.uploadedDate);
            description = `${formatFileSize(fileSize)} · Uploaded by ${userName} ${uploadDate}`;
        }

        const hasError = this.state.errors[isStaged ? file.name : file.fileId];

        const fileIcon = (
            <FileIconWrapper>
                <MdiIcon name="file" color="#fff" />
            </FileIconWrapper>
        );

        return (
            <ListItem key={`${fileName}-${index}`}>
                <div style={fileRowStyle}>
                    {thumbnailSrc ? (
                        (!isStaged && isSvg(mimeType, fileName))
                            ? <SvgThumbnail src={thumbnailSrc} alt={fileName} fallback={fileIcon} />
                            : <img src={thumbnailSrc} alt={fileName} style={thumbnailStyle} />
                    ) : fileIcon}
                    <Details>
                        <Name>{fileName}</Name>
                        <Description>{description}</Description>
                    </Details>
                    {hasError && <ErrorIcon name="alert-circle" size={18} />}
                </div>
                <DotMenu
                    edge="end"
                    items={this.buildMenuItems({ file, index, isStaged, canUpload, disabled })}
                />
            </ListItem>
        );
    }

    render() {
        const { multiple, error, required, acceptedExtensions, maxFiles: maxFilesProp, disabled, label } = this.props;
        const maxFiles = maxFilesProp ? Number(maxFilesProp) : undefined;
        const { isUploading } = this.state;

        const mergedFiles = this.getMergedFiles();
        const canUpload = !disabled && !isUploading && (
            (multiple && (!maxFiles || mergedFiles.length < maxFiles)) || !mergedFiles.length
        );

        return (
            <Container>
                <div>
                    {label && <StyledTypography>{label}</StyledTypography>}
                    <input
                        type="file"
                        ref={this.inputRef}
                        style={{ display: 'none' }}
                        accept={acceptedExtensions?.join(',')}
                        onChange={this.handleFileSelect}
                        multiple={multiple}
                    />
                    {canUpload && (
                        <Button
                            startIcon={<MdiIcon name="upload" size={18} color={muiTheme.colors.secondary.main} />}
                            size="small"
                            variant="text"
                            component="span"
                            onClick={this.openFileDialog}
                            style={{ color: muiTheme.colors.secondary.main }}
                        >
                            Upload File
                        </Button>
                    )}

                    {mergedFiles.map((file, index) => this.renderFileItem(file, index, canUpload))}

                    {isUploading && (
                        <ListItem style={{ justifyContent: 'start' }}>
                            <CircularProgress size={18} color="secondary" />
                            <UploadingText>Uploading file...</UploadingText>
                        </ListItem>
                    )}
                </div>
                {error && required && !mergedFiles.length && (
                    <FormControl error={error}>
                        <FormHelperText>Field is required</FormHelperText>
                    </FormControl>
                )}
                {this.renderEditModal()}
            </Container>
        );
    }
}

MediaField.propTypes = {
    // Injected by TaskFormTab; fired on a real media change so the unsaved-changes
    // dialog (PromptModal) triggers on navigate-away. Optional on other surfaces.
    onMediaChanged: PropTypes.func,
};

// Named exports for unit testing the real implementation (unconnected class + helpers).
export { MediaField, SvgThumbnail, isSvg };

export default connect(
    (state, ownProps) => {
        let { acceptedExtensions } = ownProps;
        if (acceptedExtensions?.length) {
            acceptedExtensions = acceptedExtensions.map(v => (!v?.includes('.') ? `.${v?.toLowerCase()}` : v?.toLowerCase()));
        }
        const entityMediaData = state.entities.entityMedia.data;
        const savedMedia = (entityMediaData && entityMediaData.id === ownProps.entityId) ? entityMediaData.media : null;
        return {
            userReferences: getUserReferences(state),
            acceptedExtensions,
            savedMedia,
        };
    },
    {
        addEntityMedia,
        deleteEntityMedia,
        loadEntityMedia,
        showToastr,
    }
)(MediaField);
